import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Box,
  Avatar,
  Container,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  CameraAlt,
  CloudUpload,
} from "@mui/icons-material";
import moment from "moment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { styled } from "@mui/system";
import { keyframes } from "@emotion/react";

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-30px);
  }
  60% {
    transform: translateY(-15px);
  }
`;

const StyledAvatar = styled(Avatar)(({ theme, bgcolor }) => ({
  width: 50,
  height: 50,
  backgroundColor: bgcolor || "blue",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  animation: `${bounce} 2s ease`,
  animationIterationCount: 2,
}));

const getSummaryData = (logsArray) => {
  console.log("whole logs", logsArray);
  return logsArray
    .map((logs) => {
      const summary = logs.reduce((acc, log) => {
        // console.log("loggg====", log)
        const auditId = log["Audit ID"];
        if (!acc[auditId]) {
          acc[auditId] = {
            auditId,
            sessionAudit: log["Session Audit"],
            imagesCaptured: 0,
            imagesUploaded: 0,
            messages: [],
            createdTime: "",
            captureTime: "",
            uploadTime: "",
            completionTime: "",
            completed: log["Completed"],
            completeButton: "",
            submitButton: "",
            auditIDProcessed: "",
            metadata: "",
            speed: log["Speed"],
          };
        }

        if (log.Message.includes("Image captured")) {
          acc[auditId].imagesCaptured += 1;
          if (!acc[auditId].captureTime) {
            acc[auditId].captureTime = log["Date Time"];
          }
        }

        if (log.Message.includes("Image uploaded")) {
          acc[auditId].imagesUploaded += 1;
          if (!acc[auditId].uploadTime) {
            acc[auditId].uploadTime = log["Date Time"];
          }
        }

        if (log.Message.includes("Audit is created")) {
          acc[auditId].messages.push("Audit is created");
          acc[auditId].createdTime = log["Date Time"];
        }

        if (log.Message.includes("Audit is completed from server")) {
          acc[auditId].messages.push("Audit is completed");
          acc[auditId].completionTime = log["Date Time"];
          acc[auditId].completionDuration = moment(log["Date Time"]).diff(
            moment(acc[auditId].createdTime),
            "seconds"
          );
        }
        if (
          log.Message.includes(
            "Audit images have been captured and the Complete button has been clicked"
          )
        ) {
          acc[auditId].messages.push(
            "Audit images have been captured and the Complete button has been clicked"
          );
          acc[auditId].completeButton = log["Date Time"];
          // acc[auditId].completionDuration = moment(log["Date Time"]).diff(
          //   moment(acc[auditId].createdTime),
          //   "seconds"
          // );
        }
        if (log.Message.includes("Audit ID processed")) {
          acc[auditId].messages.push("Audit ID processed");
          acc[auditId].auditIDProcessed = log["Date Time"];
          // acc[auditId].completionDuration = moment(log["Date Time"]).diff(
          //   moment(acc[auditId].createdTime),
          //   "seconds"
          // );
        }
        if (log.Message.includes("User click submit button")) {
          acc[auditId].messages.push("User click submit button");
          acc[auditId].submitButton = log["Date Time"];
          // acc[auditId].completionDuration = moment(log["Date Time"]).diff(
          //   moment(acc[auditId].createdTime),
          //   "seconds"
          // );
        }
        if (log.Message.includes("Metadata is uploaded")) {
          acc[auditId].messages.push("Metadata is uploaded");
          acc[auditId].metadata = log["Date Time"];
          // acc[auditId].completionDuration = moment(log["Date Time"]).diff(
          //   moment(acc[auditId].createdTime),
          //   "seconds"
          // );
        }

        return acc;
      }, {});

      return Object.values(summary);
    })
    .flat();
};

// Helper function to calculate overall summary
const getOverallSummary = (summaryData) => {
  console.log("summary data", summaryData);
  let totalCaptured = 0;
  let totalUploaded = 0;
  let totalAudits = summaryData.length;
  let totalCaptureTime = 0;
  let totalUploadTime = 0;
  let totalCompletionTime = 0;
  let totalCompletedAudits = 0;
  let totalFailedAudits = 0;
  let completedAuditsWithCompletionTime = 0;

  summaryData.forEach((data) => {
    totalCaptured += data.imagesCaptured;
    totalUploaded += data.imagesUploaded;

    if (data.createdTime && data.captureTime) {
      totalCaptureTime += moment(data.captureTime).diff(
        moment(data.createdTime),
        "seconds"
      );
    }

    if (data.captureTime && data.uploadTime) {
      totalUploadTime += moment(data.uploadTime).diff(
        moment(data.captureTime),
        "seconds"
      );
    }

    if (data.completed) {
      totalCompletedAudits += 1;
      if (data.createdTime && data.completionTime) {
        totalCompletionTime += moment(data.completionTime).diff(
          moment(data.createdTime),
          "seconds"
        );
        completedAuditsWithCompletionTime += 1;
      }
    } else {
      totalFailedAudits += 1;
    }
  });

  const averageCaptureTime = totalCaptureTime / totalAudits;
  const averageUploadTime = totalUploadTime / totalAudits;
  const averageCompletionTime = completedAuditsWithCompletionTime
    ? totalCompletionTime / completedAuditsWithCompletionTime
    : 0;

  return {
    totalCaptured,
    totalUploaded,
    totalAudits,
    averageCaptureTime,
    averageUploadTime,
    averageCompletionTime,
    totalCompletedAudits,
    totalFailedAudits,
    completedAuditsWithCompletionTime,
  };
};

const AuditLogSummary = ({ logsArray }) => {
  const summaryData = getSummaryData(logsArray);
  const overallSummary = getOverallSummary(summaryData);
  console.log("overallSummary", overallSummary);
  return (
    <Container>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 4,
          boxShadow: 3,
          backgroundColor: "#e0f7fa",
          mb: 4,
        }}
      >
        <CardContent>
          <Typography variant="h5" component="div" gutterBottom>
            Overall Summary
          </Typography>
          <Divider sx={{ marginY: 2 }} />
          <Box
            display="flex"
            justifyContent="space-around"
            alignItems="center"
            flexDirection={"row"}
            sx={{ mb: 2 }}
            gap={4}
          >
            <Box
              display="flex"
              justifyContent="space-around"
              alignItems="center"
              flexDirection={"column"}
              width="100%"
            >
              <Typography variant="h5" component="div" gutterBottom>
                Images
              </Typography>
              <Box
                display="flex"
                justifyContent="space-around"
                alignItems="center"
                flexDirection={"row"}
                width="100%"
              >
                <Chip
                  icon={<CameraAlt />}
                  label={`Total Captured: ${overallSummary.totalCaptured}`}
                  color="primary"
                  sx={{
                    fontSize: "1rem",
                    padding: "8px 12px",
                    backgroundColor: "#1976d2",
                    color: "#fff",
                  }}
                />
                <Chip
                  icon={<CloudUpload />}
                  label={`Total Uploaded: ${overallSummary.totalUploaded}`}
                  color="secondary"
                  sx={{
                    fontSize: "1rem",
                    padding: "8px 12px",
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                  }}
                />
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
              display="flex"
              justifyContent="space-around"
              alignItems="center"
              flexDirection={"column"}
              width="100%"
            >
              <Typography variant="h5" component="div" gutterBottom>
                Audits
              </Typography>
              <Box
                display="flex"
                justifyContent="space-around"
                alignItems="center"
                flexDirection={"row"}
                width="100%"
              >
                <Chip
                  icon={<CheckCircle />}
                  label={`Total: ${overallSummary.totalAudits}`}
                  color="success"
                  sx={{
                    fontSize: "1rem",
                    padding: "8px 12px",
                    backgroundColor: "#1976d2",
                    color: "#fff",
                  }}
                />
                <Chip
                  icon={<CheckCircle />}
                  label={`Completed: ${overallSummary.totalCompletedAudits}`}
                  color="success"
                  sx={{
                    fontSize: "1rem",
                    padding: "8px 12px",
                    backgroundColor: "#388e3c",
                    color: "#fff",
                  }}
                />
                <Chip
                  icon={<Cancel />}
                  label={`Failed: ${overallSummary.totalFailedAudits}`}
                  color="error"
                  sx={{
                    fontSize: "1rem",
                    padding: "8px 12px",
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Divider sx={{ marginY: 2 }} />
          {/* <Typography variant="body1" color="text.secondary">
            <strong>Average Capture Time:</strong>{" "}
            {overallSummary.averageCaptureTime.toFixed(2)} seconds
          </Typography>
          <Typography variant="body1" color="text.secondary">
            <strong>Average Upload Time:</strong>{" "}
            {overallSummary.averageUploadTime.toFixed(2)} seconds
          </Typography>
          <Typography variant="body1" color="text.secondary">
            <strong>Average Completion Time:</strong>{" "}
            {overallSummary.averageCompletionTime
              ? overallSummary.averageCompletionTime.toFixed(2)
              : "N/A"}{" "}
            seconds
          </Typography> */}
          <Grid container spacing={0} justifyContent="space-around">
            <Grid item>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection={"column"}
              >
                <StyledAvatar bgcolor="#1976d2">
                  <AccessTimeIcon fontSize="large" />
                </StyledAvatar>
                <Typography variant="h5" color="textPrimary">
                  {overallSummary.averageCaptureTime.toFixed(2)} seconds
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Average Image Capture Time
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection={"column"}
              >
                <StyledAvatar bgcolor="#1976d2">
                  <CloudUploadIcon fontSize="large" />
                </StyledAvatar>
                <Typography variant="h5" color="textPrimary">
                  {overallSummary.averageUploadTime.toFixed(2)} seconds
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Average Image Upload Time
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection={"column"}
              >
                <StyledAvatar bgcolor="#1976d2">
                  <CheckCircleIcon fontSize="large" />
                </StyledAvatar>
                <Typography variant="h5" color="textPrimary">
                  {overallSummary.averageCompletionTime.toFixed(2)} seconds
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Average Audit Completion Time
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{}}>
        {summaryData.map((data, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              variant="outlined"
              sx={{ borderRadius: 4, boxShadow: 3, backgroundColor: "#f0f0f0" }}
            >
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {/* <Typography 
                    variant="h6" 
                    component="div" 
                    gutterBottom
                   
                  >
                    Audit ID: {data.auditId}
                  </Typography> */}
                  <Typography
                    fontSize={16}
                    fontWeight="bold"
                    component="div"
                    gutterBottom
                  >
                    Audit ID: {data.auditId}
                  </Typography>

                  {/* <Avatar
                    sx={{
                      bgcolor: data.messages.includes("Audit is completed")
                        ? "green"
                        : "red",
                    }}
                  >
                    {data.messages.includes("Audit is completed") ? (
                      <CheckCircle />
                    ) : (
                      <Cancel />
                    )}
                  </Avatar> */}
                  <Avatar
                    sx={{
                      bgcolor: data.completed ? "green" : "red",
                      fontSize: "10",
                      height: "24px",
                      width: "32px",
                    }}
                  >
                    {data.completed ? (
                      <CheckCircle height={2} width={2} />
                    ) : (
                      <Cancel />
                    )}
                    {console.log("data complete", data.completed)}
                  </Avatar>
                </Box>
                <Divider sx={{ marginY: 2 }} />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Chip
                    icon={<CameraAlt />}
                    label={`Captured: ${data.imagesCaptured}`}
                    color="primary"
                    sx={{
                      fontSize: "1rem",
                      padding: "8px 12px",
                      backgroundColor: "#1976d2",
                      color: "#fff",
                    }}
                  />
                  <Chip
                    icon={<CloudUpload />}
                    label={`Uploaded: ${data.imagesUploaded}`}
                    color="secondary"
                    sx={{
                      fontSize: "1rem",
                      padding: "8px 12px",
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                    }}
                  />
                </Box>
                <Divider sx={{ marginY: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>Session Audit:</strong> {data.sessionAudit}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Audit Created:</strong> {data.createdTime}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>clicked complete button:</strong>{" "}
                  {data.completeButton ? `${data.completeButton}` : "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>clicked submit button:</strong>{" "}
                  {data.submitButton ? `${data.submitButton}` : "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Audit Completed:</strong>{" "}
                  {data.completionTime ? data.completionTime : "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Audit ID Processed:</strong>{" "}
                  {data.auditIDProcessed ? data.auditIDProcessed : "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>TAT of Audit:</strong>{" "}
                  {data.completionDuration
                    ? `${data.completionDuration.toFixed(2)} seconds`
                    : "N/A"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>Images Captured:</strong>
                  {data.imagesCaptured ? data.imagesCaptured : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Images Uploaded:</strong>{" "}
                  {data.imagesUploaded ? data.imagesUploaded : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Metadata Uploaded:</strong>{" "}
                  {data.metadata ? data.metadata : "N/A"}
                </Typography>

                {/* <Typography variant="body2" color="text.secondary"> */}
                {/* <strong>Speed:</strong>{" "} */}
                {data.speed ? data.speed : 0}
                {/* </Typography> */}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default AuditLogSummary;
